import { hasMultipleTabsOpen } from './MultiTabDetector/multiTabDetector';
import {
  identifySegmentUser,
  logSegmentPageView,
  resetSegmentUser,
  trackSegmentEvent,
} from './Segment/segment';
import type { IIdentifyUserProps, TEventProperties } from './types';

/**
 * Tracks an event with all web analytics tools we use.
 * Wraps call in a 0-length timeout to force event logging
 * to occur after all rendering/loading has completed.
 * @param event The name of the event
 * @param properties Supporting event parameters (can be anything)
 */
export const trackAnalyticsEvent = (event: string, properties: TEventProperties = {}): void => {
  setTimeout(() => {
    const eventProperties = {
      ...properties,
      ...(hasMultipleTabsOpen() && { multiple_tabs_open: true }),
    };
    trackSegmentEvent(event, eventProperties);
  }, 0);
};

/**
 * Identifies a previously unknown (logged-out) user for analytics purposes.
 * Wraps call in a 0-length timeout to force event logging
 * to occur after all rendering/loading has completed.
 * @param patientId The patient ID of the user to identify
 * @param isColorEmployee Define if the user is Color employee or not. This information is used to
 * set Segment traits.
 */
export const identifyAnalyticsUser = (props: IIdentifyUserProps): void => {
  // Segment uses the patient ID as the user identifier, see the method documentation for more info
  setTimeout(() => {
    identifySegmentUser(props);
  }, 0);
};

/**
 * Clear user data for analytics purposes.
 * Wraps call in a 0-length timeout to force event logging
 * to occur after all rendering/loading has completed.
 */
export const resetAnalyticsUser = (): void => {
  setTimeout(() => {
    resetSegmentUser();
  }, 0);
};

/**
 * Registers with analytics providers that the user has seen a page.
 * Wraps call in a 0-length timeout to force event logging
 * to occur after all rendering/loading has completed.
 * @param name Optional name for the page
 * @param properties Extra properties to attach to the log
 */
export const logAnalyticsPageView = (name?: string, properties?: TEventProperties): void => {
  setTimeout(() => {
    const eventProperties = {
      ...properties,
      ...(hasMultipleTabsOpen() && { multiple_tabs_open: true }),
    };
    logSegmentPageView(name, eventProperties);
  }, 0);
};
